import { inject } from '@angular/core';
import { ResolveFn, Routes } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ProjectService } from './services/project.service';
import { ProjectRoleService } from './services/project-role.service';
import { RequirementsService } from './services/requirements.service';
import { AppBreadcrumb } from './layout/breadcrumb/app-breadcrumb.component';
import { authGuard } from './core/auth/auth.guard';
import { logoutGuard } from './core/auth/logout.guard';
import { platformAdminGuard } from './core/auth/platform-admin.guard';
import { tenantAdminGuard } from './core/auth/tenant-admin.guard';
import { LoginComponent } from './login/login.component';
import { MainLayoutComponent } from './layout/main-layout/main-layout.component';
import { HomeComponent } from './home/home.component';
import { DashboardComponent } from './dashboard/dashboard.component';
import { TestResultsComponent } from './dashboard/test-results/test-results.component';
import { canDeactivateTestResults } from './dashboard/test-results/can-deactivate-test-results.guard';
import { TestResultDetailComponent } from './dashboard/test-result-detail/test-result-detail.component';
import { TestResultHistoryComponent } from './dashboard/test-result-history/test-result-history.component';
import { RequirementsComponent } from './dashboard/requirements/requirements.component';
import { RequirementFormComponent } from './dashboard/requirements/requirement-form/requirement-form.component';
import { canDeactivateRequirementForm } from './dashboard/requirements/requirement-form/can-deactivate-requirement-form.guard';
import { RequirementHistoryComponent } from './dashboard/requirements/requirement-history/requirement-history.component';
import { TestCaseComponent } from './dashboard/test-case/test-case.component';
import { TestCaseFormComponent } from './dashboard/test-case/test-case-form/test-case-form.component';
import { canDeactivateTestCaseForm } from './dashboard/test-case/test-case-form/can-deactivate-test-case-form.guard';
import { TestlaufUebersichtComponent } from './dashboard/testlauf-uebersicht/testlauf-uebersicht.component';
import { TestlaufNeuComponent } from './dashboard/testlauf-neu/testlauf-neu.component';
import { TestlaufDurchlaeufeComponent } from './dashboard/testlauf-durchlaeufe/testlauf-durchlaeufe.component';
import { TestlaufRunnerComponent } from './dashboard/testlauf-runner/testlauf-runner.component';
import { TestlaufErgebnisseComponent } from './dashboard/testlauf-ergebnisse/testlauf-ergebnisse.component';
import { ciMappingRedirectGuard } from './dashboard/ci-mapping/ci-mapping-redirect.guard';
import { canDeactivateTestlaufRunner } from './dashboard/testlauf-runner/can-deactivate-testlauf-runner.guard';
import { canDeactivateTestlaufNeu } from './dashboard/testlauf-neu/can-deactivate-testlauf-neu.guard';
import { TestStepComponent } from './dashboard/test-step/test-step.component';
import { TestStepFormComponent } from './dashboard/test-step/test-step-form/test-step-form.component';
import { canDeactivateTestStepForm } from './dashboard/test-step/test-step-form/can-deactivate-test-step-form.guard';
import { ModuleComponent } from './dashboard/module/module.component';
import { ModuleFormComponent } from './dashboard/module/module-form/module-form.component';
import { canDeactivateModuleForm } from './dashboard/module/module-form/can-deactivate-module-form.guard';
import { TestPlansComponent } from './dashboard/test-plans/test-plans.component';
import { TestPlanFormComponent } from './dashboard/test-plans/test-plan-form/test-plan-form.component';
import { canDeactivateTestPlanForm } from './dashboard/test-plans/test-plan-form/can-deactivate-test-plan-form.guard';
import { TestPlanReportComponent } from './dashboard/test-plans/test-plan-report/test-plan-report.component';
import { TestdatenblattComponent } from './dashboard/testdatenblatt/testdatenblatt.component';
import { TestdatenblattFormComponent } from './dashboard/testdatenblatt/testdatenblatt-form/testdatenblatt-form.component';
import { canDeactivateTestdatenblattForm } from './dashboard/testdatenblatt/testdatenblatt-form/can-deactivate-testdatenblatt-form.guard';
import { ObjectMappingListComponent } from './dashboard/object-mapping/object-mapping-list.component';
import { ObjectMappingDetailComponent } from './dashboard/object-mapping/object-mapping-detail.component';
import { RecordingLiveComponent } from './dashboard/object-mapping/recording-live/recording-live.component';
import { CaptureReviewComponent } from './dashboard/object-mapping/capture-review/capture-review.component';
import { DefectsPageComponent } from './dashboard/defects/defects-page.component';
import { DefectDetailComponent } from './dashboard/defects/defect-detail.component';
import { ClientManagementComponent } from './client-management/client-management.component';
import { CustomerDetailComponent } from './customer-detail/customer-detail.component';
import { UserEditPageComponent } from './customer-detail/user-edit-page/user-edit-page.component';
import { canDeactivateUserEditPage } from './customer-detail/user-edit-page/can-deactivate-user-edit-page.guard';
import { UserInvitePageComponent } from './customer-detail/user-invite-page/user-invite-page.component';
import { canDeactivateUserInvitePage } from './customer-detail/user-invite-page/can-deactivate-user-invite-page.guard';
import { CustomerFormComponent } from './client-management/customer-form/customer-form.component';
import { canDeactivateCustomerForm } from './client-management/customer-form/can-deactivate-customer-form.guard';
import { SettingsComponent } from './settings/settings.component';
import { AccountComponent } from './account/account.component';
import { ProjectFormComponent } from './projects/project-form/project-form.component';
import { canDeactivateProjectForm } from './guards/can-deactivate-project-form.guard';
import { canDeactivateSettings } from './guards/can-deactivate-settings.guard';

const projectNameResolver: ResolveFn<string> = (route) => {
  const projectId = route.paramMap.get('projectId')!;
  return inject(ProjectService)
    .getById(projectId)
    .pipe(
      map((project) => project.name),
      catchError(() => of(projectId)),
    );
};

const projectRoleResolver: ResolveFn<boolean> = (route): Observable<boolean> =>
  inject(ProjectRoleService).ensureLoaded(route.paramMap.get('projectId'));

/** Fügt den Anforderungs-Key (`<key>`) als eigenes Breadcrumb-Segment vor der aktuellen Seite ein. */
const requirementKeyBreadcrumbResolver: ResolveFn<AppBreadcrumb[]> = (route) => {
  const projectId = route.paramMap.get('projectId')!;
  const requirementId = route.paramMap.get('id')!;
  const url = `/projects/${projectId}/requirements/${requirementId}/edit`;
  return inject(RequirementsService)
    .getById(projectId, requirementId)
    .pipe(
      map((requirement) => [{ label: requirement.extId || requirement.title || requirementId, url }]),
      catchError(() => of([{ label: requirementId, url }])),
    );
};

export const routes: Routes = [
  {
    path: 'login',
    component: LoginComponent,
  },
  {
    path: 'logout',
    canActivate: [logoutGuard],
    component: LoginComponent,
  },
  {
    path: 'invitations/accept',
    loadComponent: () =>
      import('./invitations/invitation-accept/invitation-accept.component').then((m) => m.InvitationAcceptComponent),
  },
  {
    path: '',
    component: MainLayoutComponent,
    canActivate: [authGuard],
    children: [
      {
        path: '',
        component: HomeComponent,
        data: { breadcrumb: 'header.breadcrumb.home' },
      },
      {
        path: 'dashboard',
        component: DashboardComponent,
        data: {
          breadcrumb: 'header.breadcrumb.dashboard',
          breadcrumbPrefix: [{ label: 'header.breadcrumb.home', url: '/' }],
        },
      },
      {
        path: 'client-management',
        canActivate: [platformAdminGuard],
        data: { breadcrumb: 'header.breadcrumb.clientManagement' },
        children: [
          {
            path: '',
            component: ClientManagementComponent,
            data: { breadcrumb: '' },
          },
          {
            path: 'new',
            component: CustomerFormComponent,
            canDeactivate: [canDeactivateCustomerForm],
            data: { breadcrumb: 'header.breadcrumb.customerFormCreate' },
          },
          {
            path: ':customerId/bearbeiten',
            component: CustomerFormComponent,
            canDeactivate: [canDeactivateCustomerForm],
            data: { breadcrumb: 'header.breadcrumb.customerFormEdit' },
          },
          {
            path: ':customerId',
            data: { breadcrumb: 'header.breadcrumb.customerDetail' },
            children: [
              {
                path: '',
                component: CustomerDetailComponent,
                data: { breadcrumb: '' },
              },
              {
                path: 'users/neu',
                component: UserInvitePageComponent,
                canDeactivate: [canDeactivateUserInvitePage],
                data: { breadcrumb: 'header.breadcrumb.userInvite' },
              },
              {
                path: 'users/:userId/bearbeiten',
                component: UserEditPageComponent,
                canDeactivate: [canDeactivateUserEditPage],
                data: { breadcrumb: 'header.breadcrumb.userEdit' },
              },
            ],
          },
        ],
      },
      {
        path: 'settings',
        component: SettingsComponent,
        canActivate: [tenantAdminGuard],
        canDeactivate: [canDeactivateSettings],
        data: { breadcrumb: 'header.breadcrumb.settings' },
      },
      {
        path: 'account',
        component: AccountComponent,
        data: { breadcrumb: 'header.breadcrumb.account' },
      },
      {
        path: 'postfach',
        loadComponent: () => import('./postfach/postfach-page.component').then((m) => m.PostfachPageComponent),
        data: { breadcrumb: 'header.breadcrumb.postfach' },
      },
      {
        path: 'runners',
        loadComponent: () => import('./runners/runners-page.component').then((m) => m.RunnersPageComponent),
        data: { breadcrumb: 'header.breadcrumb.runners' },
      },
      {
        path: 'projects',
        data: { breadcrumb: 'header.breadcrumb.dashboard' },
        children: [
          { path: '', redirectTo: '/dashboard', pathMatch: 'full' },
          {
            path: 'new',
            component: ProjectFormComponent,
            canDeactivate: [canDeactivateProjectForm],
            data: { breadcrumb: 'header.breadcrumb.projectFormCreate' },
          },
          {
            path: ':projectId',
            providers: [ProjectRoleService],
            resolve: { breadcrumb: projectNameResolver, roleReady: projectRoleResolver },
            children: [
              { path: '', redirectTo: 'requirements', pathMatch: 'full' },
              {
                path: 'edit',
                component: ProjectFormComponent,
                canDeactivate: [canDeactivateProjectForm],
                data: { breadcrumb: 'header.breadcrumb.projectFormEdit' },
              },
              {
                path: 'test-results',
                data: { breadcrumb: 'header.breadcrumb.testResults' },
                children: [
                  {
                    path: '',
                    component: TestResultsComponent,
                    canDeactivate: [canDeactivateTestResults],
                    data: { breadcrumb: '' },
                  },
                  {
                    path: 'history/:testCaseId',
                    component: TestResultHistoryComponent,
                    data: { breadcrumb: 'header.breadcrumb.testResultHistory' },
                  },
                  {
                    path: ':runId',
                    component: TestResultDetailComponent,
                    data: { breadcrumb: 'header.breadcrumb.testResultDetail' },
                  },
                ],
              },
              {
                path: 'test-cases',
                data: { breadcrumb: 'header.breadcrumb.testCase' },
                children: [
                  { path: '', component: TestCaseComponent, data: { breadcrumb: '' } },
                  {
                    path: 'new',
                    component: TestCaseFormComponent,
                    canDeactivate: [canDeactivateTestCaseForm],
                    data: { breadcrumb: 'header.breadcrumb.testCaseFormCreate' },
                  },
                  {
                    path: ':id/edit',
                    component: TestCaseFormComponent,
                    canDeactivate: [canDeactivateTestCaseForm],
                    data: { breadcrumb: 'header.breadcrumb.testCaseFormEdit' },
                  },
                ],
              },
              {
                path: 'test-runs',
                data: { breadcrumb: 'header.breadcrumb.testRun' },
                children: [
                  {
                    path: '',
                    component: TestlaufUebersichtComponent,
                    data: { breadcrumb: '' },
                  },
                  // Vor ':runId' — sonst matcht die Detailseite und liest 'new' als Lauf-ID.
                  {
                    path: 'new',
                    component: TestlaufNeuComponent,
                    canDeactivate: [canDeactivateTestlaufNeu],
                    data: { breadcrumb: 'header.breadcrumb.testRunFormCreate' },
                  },
                  {
                    path: ':runId/edit',
                    component: TestlaufNeuComponent,
                    canDeactivate: [canDeactivateTestlaufNeu],
                    data: { breadcrumb: 'header.breadcrumb.testRunFormEdit' },
                  },
                  // Vor ':runId' — die Zwischenebene ist ein eigener Pfad unter dem Lauf.
                  {
                    path: ':runId/executions',
                    component: TestlaufDurchlaeufeComponent,
                    data: { breadcrumb: 'header.breadcrumb.testRunExecutions' },
                  },
                  // Die CI-Zuordnung lebt im Runner (Filter „Nicht zugeordnet“); der Pfad bleibt
                  // für alte Links bestehen.
                  {
                    path: ':runId/ci-mapping',
                    canActivate: [ciMappingRedirectGuard],
                    children: [],
                  },
                  {
                    path: ':runId/executions/:executionId/results',
                    component: TestlaufErgebnisseComponent,
                    data: { breadcrumb: 'header.breadcrumb.testRunResults' },
                  },
                  {
                    path: ':runId/executions/:executionId',
                    component: TestlaufRunnerComponent,
                    canDeactivate: [canDeactivateTestlaufRunner],
                    data: { breadcrumb: 'header.breadcrumb.testRunner' },
                  },
                  // Die Durchfuehrung haengt am Durchlauf, nicht am Lauf (NEX-1042). Der
                  // Lauf-Pfad bleibt als Einsprung fuer alte Links bestehen und fuehrt
                  // auf dessen Durchlaeufe.
                  {
                    path: ':runId',
                    redirectTo: ':runId/executions',
                    pathMatch: 'full',
                  },
                ],
              },
              {
                path: 'test-steps',
                data: { breadcrumb: 'header.breadcrumb.testStep' },
                children: [
                  { path: '', component: TestStepComponent, data: { breadcrumb: '' } },
                  {
                    path: 'new',
                    component: TestStepFormComponent,
                    canDeactivate: [canDeactivateTestStepForm],
                    data: { breadcrumb: 'header.breadcrumb.testStepFormCreate' },
                  },
                  {
                    path: ':id/edit',
                    component: TestStepFormComponent,
                    canDeactivate: [canDeactivateTestStepForm],
                    data: { breadcrumb: 'header.breadcrumb.testStepFormEdit' },
                  },
                ],
              },
              {
                path: 'test-modules',
                data: { breadcrumb: 'header.breadcrumb.module' },
                children: [
                  { path: '', component: ModuleComponent, data: { breadcrumb: '' } },
                  {
                    path: 'new',
                    component: ModuleFormComponent,
                    canDeactivate: [canDeactivateModuleForm],
                    data: { breadcrumb: 'header.breadcrumb.moduleNew' },
                  },
                  {
                    path: ':moduleId/edit',
                    component: ModuleFormComponent,
                    canDeactivate: [canDeactivateModuleForm],
                    data: { breadcrumb: 'header.breadcrumb.moduleEdit' },
                  },
                ],
              },
              {
                path: 'test-plans',
                data: { breadcrumb: 'header.breadcrumb.testPlan' },
                children: [
                  { path: '', component: TestPlansComponent, data: { breadcrumb: '' } },
                  {
                    path: 'new',
                    component: TestPlanFormComponent,
                    canDeactivate: [canDeactivateTestPlanForm],
                    data: { breadcrumb: 'header.breadcrumb.testPlanNew' },
                  },
                  {
                    path: ':planId/edit',
                    component: TestPlanFormComponent,
                    canDeactivate: [canDeactivateTestPlanForm],
                    data: { breadcrumb: 'header.breadcrumb.testPlanEdit' },
                  },
                  {
                    path: ':planId/report',
                    component: TestPlanReportComponent,
                    data: { breadcrumb: 'header.breadcrumb.testPlanReport' },
                  },
                ],
              },
              {
                path: 'testdatenblaetter',
                data: { breadcrumb: 'header.breadcrumb.testdatenblatt' },
                children: [
                  { path: '', component: TestdatenblattComponent, data: { breadcrumb: '' } },
                  {
                    path: 'neu',
                    component: TestdatenblattFormComponent,
                    canDeactivate: [canDeactivateTestdatenblattForm],
                    data: { breadcrumb: 'header.breadcrumb.testdatenblattFormCreate' },
                  },
                  {
                    path: ':tdbId/bearbeiten',
                    component: TestdatenblattFormComponent,
                    canDeactivate: [canDeactivateTestdatenblattForm],
                    data: { breadcrumb: 'header.breadcrumb.testdatenblattFormEdit' },
                  },
                ],
              },
              {
                path: 'object-mappings',
                data: { breadcrumb: 'header.breadcrumb.objectMappings' },
                children: [
                  { path: '', component: ObjectMappingListComponent, data: { breadcrumb: '' } },
                  {
                    path: ':mappingId',
                    component: ObjectMappingDetailComponent,
                    data: { breadcrumb: 'header.breadcrumb.objectMappingDetail' },
                  },
                  {
                    path: ':mappingId/recording/:jobId',
                    component: RecordingLiveComponent,
                    data: { breadcrumb: 'header.breadcrumb.objectMappingRecording' },
                  },
                  {
                    path: ':mappingId/review',
                    component: CaptureReviewComponent,
                    data: { breadcrumb: 'header.breadcrumb.objectMappingReview' },
                  },
                ],
              },
              {
                path: 'defects',
                data: { breadcrumb: 'defect.list.breadcrumb' },
                children: [
                  { path: '', component: DefectsPageComponent, data: { breadcrumb: '' } },
                  {
                    path: ':defectId',
                    component: DefectDetailComponent,
                    data: { breadcrumb: 'defect.detail.breadcrumb' },
                  },
                ],
              },
              {
                path: 'requirements',
                data: { breadcrumb: 'header.breadcrumb.requirements' },
                children: [
                  {
                    path: '',
                    component: RequirementsComponent,
                    data: { breadcrumb: '' },
                  },
                  {
                    path: 'new',
                    component: RequirementFormComponent,
                    canDeactivate: [canDeactivateRequirementForm],
                    data: { breadcrumb: 'header.breadcrumb.requirementFormCreate' },
                  },
                  {
                    path: ':id/edit',
                    component: RequirementFormComponent,
                    canDeactivate: [canDeactivateRequirementForm],
                    data: { breadcrumb: 'header.breadcrumb.requirementFormEdit' },
                  },
                  {
                    path: ':id/historie',
                    component: RequirementHistoryComponent,
                    resolve: { breadcrumbPrefix: requirementKeyBreadcrumbResolver },
                    data: { breadcrumb: 'header.breadcrumb.requirementHistory' },
                  },
                ],
              },
              // Legacy-Redirects: alte Singular-URLs auf die neuen Plural-Pfade.
              // pathMatch 'prefix' haengt Restsegmente an (z. B. /new, /:id/edit).
              { path: 'test-case', redirectTo: 'test-cases', pathMatch: 'prefix' },
              { path: 'test-step', redirectTo: 'test-steps', pathMatch: 'prefix' },
              { path: 'module', redirectTo: 'test-modules', pathMatch: 'prefix' },
            ],
          },
        ],
      },
    ],
  },
  { path: '**', redirectTo: 'login' },
];
